import {
  Star,
  LayoutList,
  Search,
  UserCircle,
  Mail,
  Trophy,
  Sparkles,
  CalendarDays,
  ClipboardList,
  Send,
  RefreshCw,
  GraduationCap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Client-side UI bookkeeping for server-driven next steps.
 * The server decides WHICH step to show; the client only remembers
 * dismissals (7-day window) and impression counts.
 */
export interface TipState {
  dismissed: Record<string, number>; // stepKey -> dismissal timestamp
  impressions: Record<string, number>; // stepKey -> show count
}

export interface StepCopy {
  title: string;
  description: string;
  icon: LucideIcon;
}

/**
 * User-facing display copy for each server stepKey.
 * The server's `rationale` is internal-only and must never be shown —
 * this map is the single source of user-facing next-step copy.
 */
export const STEP_COPY: Record<string, StepCopy> = {
  surface_matches_invite_profile_fill: {
    title: "See your school matches",
    description: "Check out programs that fit you, then add profile details to sharpen the matches",
    icon: Search,
  },
  complete_profile_to_unlock_matches: {
    title: "Unlock your school matches",
    description: "Add a few profile basics so we can match you with the right programs",
    icon: UserCircle,
  },
  resume_abandoned_thread: {
    title: "Pick up where you left off",
    description: "You have an unfinished conversation with your coach — jump back in",
    icon: Sparkles,
  },
  send_oldest_draft: {
    title: "Send your drafted coach email",
    description: "You have a draft waiting — give it a final look and hit send",
    icon: Send,
  },
  complete_profile_basics: {
    title: "Complete your profile basics",
    description: "Add your GPA, grad year, and contact info so coaches can evaluate you",
    icon: UserCircle,
  },
  pivot_to_unranked_friendly: {
    title: "Explore unranked-friendly programs",
    description: "Plenty of great programs recruit players without a ranking — find yours",
    icon: GraduationCap,
  },
  get_ranked_urgently: {
    title: "Get ranked now",
    description: "Play ranked events soon so college coaches can find and evaluate you",
    icon: Trophy,
  },
  plan_tournament_schedule: {
    title: "Plan your tournament schedule",
    description: "Map out events that boost your ranking and get you seen by coaches",
    icon: CalendarDays,
  },
  generate_first_school_list: {
    title: "Build your first school list",
    description: "Get a starter list of programs that fit your game and goals",
    icon: LayoutList,
  },
  track_results_to_ranking: {
    title: "Track your tournament results",
    description: "Log your results so your ranking reflects your latest play",
    icon: Trophy,
  },
  generate_targeted_school_list: {
    title: "Build a targeted school list",
    description: "Get a list of programs matched to your ranking and academics",
    icon: LayoutList,
  },
  generate_first_school_list_competitive: {
    title: "Build your first school list",
    description: "See competitive programs where your game stacks up",
    icon: LayoutList,
  },
  generate_first_school_list_explorative: {
    title: "Explore schools that could fit",
    description: "Browse a wide mix of programs to see what feels right",
    icon: Search,
  },
  expand_school_list: {
    title: "Add more schools to your list",
    description: "Broaden your list so you have plenty of good options",
    icon: Star,
  },
  fill_tier_gap: {
    title: "Balance your school list",
    description: "Round out your dream, realistic, and safety tiers",
    icon: ClipboardList,
  },
  start_first_outreach: {
    title: "Send your first coach email",
    description: "Introduce yourself to a coach on your list — we'll help you draft it",
    icon: Mail,
  },
  start_first_outreach_urgent: {
    title: "Reach out to coaches now",
    description: "The recruiting window is moving — get your first email out today",
    icon: Mail,
  },
  expand_outreach_breadth: {
    title: "Email more coaches",
    description: "Reach out to more schools on your list to boost your chances of replies",
    icon: Mail,
  },
  revisit_subjects_or_targets: {
    title: "Refresh your outreach approach",
    description: "Try new subject lines or different schools to earn more responses",
    icon: RefreshCw,
  },
  strengthen_outreach_content: {
    title: "Strengthen your coach emails",
    description: "Sharpen your emails with recent results, video, and specifics coaches want",
    icon: Mail,
  },
  send_followup: {
    title: "Follow up with coaches",
    description: "A polite follow-up keeps you on a coach's radar",
    icon: Send,
  },
  maintain_consistency: {
    title: "Keep your momentum going",
    description: "Stay active — update results and check in with coaches regularly",
    icon: RefreshCw,
  },
};

/**
 * Resolve display copy for a stepKey. Unknown keys (new server steps this
 * client doesn't know yet) gracefully fall back to the server's ctaLabel.
 */
export function getStepDisplay(stepKey: string, ctaLabel: string): StepCopy {
  return (
    STEP_COPY[stepKey] ?? {
      title: ctaLabel,
      description: ctaLabel,
      icon: Sparkles,
    }
  );
}
